import { inject, Injectable } from "@angular/core";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { GastBlobRequestModel, GastRequestModel, GastRequestWithBodyModel } from "../../models";
import { NgxSpinnerService } from "ngx-spinner";
import { ToastrService } from "ngx-toastr";

@Injectable({
  providedIn: "root",
})
export class HttpService {
  readonly #httpClient = inject(HttpClient);
  readonly #spinnerService = inject(NgxSpinnerService);
  readonly #toasterService = inject(ToastrService);
  /**
   * Performs a http <code>GET</code> request and handles eventual error
   * @param request {@link GastRequestModel} contains url and success callback
   * @param errorCallback optional fallback fn
   */
  get<R>(request: GastRequestModel<R>, errorCallback?: (response: HttpErrorResponse) => void) {
    this.#httpClient
      .get<R>(request.url, { withCredentials: true })
      .subscribe({ next: request.callback, error: (e) => (errorCallback ? errorCallback(e) : this.#handleError(e)) });
  }
  /**
   * Performs a http <code>GET</code> request for <code>blob</code> response and handles eventual error
   * @param request {@link GastBlobRequestModel} contains url and success callback
   * @param errorCallback optional fallback fn
   */
  getBlob(request: GastBlobRequestModel, errorCallback?: (response: HttpErrorResponse) => void) {
    this.#httpClient.get(request.url, { withCredentials: true, responseType: "blob" }).subscribe({
      next: request.callback,
      error: (e) => (errorCallback ? errorCallback(e) : this.#handleError(e)),
    });
  }
  /**
   * Performs a http <code>POST</code> request and handles eventual error
   * @param request {@link GastRequestWithBodyModel} contains url, payload and success callback
   * @param errorCallback optional fallback fn
   */
  post<R, B>(request: GastRequestWithBodyModel<R, B>, errorCallback?: (error: HttpErrorResponse) => void) {
    this.#httpClient.post<R>(request.url, request.body || {}, { withCredentials: true }).subscribe({
      next: request.callback,
      error: (e) => (errorCallback ? errorCallback(e) : this.#handleError(e)),
    });
  }
  /**
   * Performs a http <code>PUT</code> request and handles eventual error
   * @param request {@link GastRequestWithBodyModel} contains url, payload and success callback
   * @param errorCallback optional fallback fn
   */
  put<R, B>(request: GastRequestWithBodyModel<R, B>, errorCallback?: (error: HttpErrorResponse) => void) {
    this.#httpClient.put<R>(request.url, request.body || {}, { withCredentials: true }).subscribe({
      next: request.callback,
      error: (e) => (errorCallback ? errorCallback(e) : this.#handleError(e)),
    });
  }
  /**
   * Performs a http <code>DELETE</code> request and handles eventual error
   * @param request {@link GastRequestModel} contains url and success callback
   * @param errorCallback optional fallback fn
   */
  delete<R>(request: GastRequestModel<R>, errorCallback?: (error: HttpErrorResponse) => void) {
    this.#httpClient.delete<R>(request.url, { withCredentials: true }).subscribe({
      next: request.callback,
      error: (e) => (errorCallback ? errorCallback(e) : this.#handleError(e)),
    });
  }

  #handleError(response: HttpErrorResponse): void {
    this.#spinnerService.hide();
    if (response.status !== 0) {
      this.#toasterService.error(response.error ? response.error.message : response.message, "Fehler");
    }
  }
}
